import { CogIcon, XIcon } from "@heroicons/react/solid";
import { Transition, Dialog } from "@headlessui/react";
import { Fragment, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
import Image from "next/image";

import "swiper/css";
import "swiper/css/navigation";
import ContentLoader from "react-content-loader";
import ReactMarkdown from "react-markdown";

export default function Product({
  Name,
  photos,
  description,
  specSheet,
  baseUrl,
  translations,
}) {
  const [openImage, setOpenImage] = useState(undefined);

  return (
    <div className="rounded shadow dark:shadow-neutral-800">
      {photos.data && (
        <Swiper
          modules={[Navigation]}
          spaceBetween={10}
          slidesPerView={1}
          navigation
        >
          {photos?.data?.map((photo) => {
            const imageId = `image-${photo.id}`;

            if (
              !photo ||
              !photo.attributes.formats.medium ||
              !photo.attributes.formats.large
            ) {
              return <div key={imageId}></div>;
            }

            return (
              <SwiperSlide key={imageId}>
                <button
                  onClick={() => {
                    setOpenImage(imageId);
                  }}
                >
                  <LightBox
                    image={`${baseUrl}${photo.attributes.formats.large?.url}`}
                    width={photo.attributes.formats.large?.width}
                    height={photo.attributes.formats.large?.height}
                    isOpen={openImage === imageId}
                    onClose={() => setOpenImage(undefined)}
                  />
                  <Image
                    className="rounded-t select-none"
                    src={`${baseUrl}${photo.attributes?.formats?.medium?.url}`}
                    width={photo.attributes?.formats?.medium?.width}
                    height={photo.attributes?.formats?.medium?.height}
                    alt={Name}
                  />
                </button>
              </SwiperSlide>
            );
          })}
        </Swiper>
      )}
      <article className="px-4 py-4">
        <h3 className="text-lg font-semibold">{Name}</h3>
        <div>
          <TechnicalSpecifications
            description={description}
            specSheet={specSheet}
            translations={translations}
          />
        </div>
      </article>
    </div>
  );
}

function TechnicalSpecifications({ description, specSheet, translations }) {
  let [isOpen, setIsOpen] = useState(false);

  function closeModal() {
    setIsOpen(false);
  }

  function openModal() {
    setIsOpen(true);
  }

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="flex items-center space-x-2 transition focus:outline-none focus:text-default focus:underline"
      >
        <CogIcon className="w-6 h-6 text-default" />
        <p>{translations[0]}</p>
      </button>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25 dark:bg-opacity-75" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-full p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl p-6 overflow-hidden text-left align-middle transition-all transform bg-white rounded shadow-xl dark:bg-neutral-800">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6"
                  >
                    {translations[0]}
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm ">
                      {description ? (
                        <ReactMarkdown className="whitespace-pre-wrap">
                          {description}
                        </ReactMarkdown>
                      ) : (
                        <p>N/A</p>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center justify-between w-full mt-4">
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-center text-white rounded bg-default focus:outline-none"
                      onClick={closeModal}
                    >
                      {translations[2]}
                    </button>
                    {specSheet ? (
                      <a
                        target="_blank"
                        rel="noreferrer"
                        href={specSheet}
                        className="text-sm transition hover:text-default"
                      >
                        {translations[1]}
                      </a>
                    ) : null}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}

function LightBox({ image, width, height, isOpen, onClose }) {
  let [isLoaded, setIsLoaded] = useState(false);

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => onClose()}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25 dark:bg-opacity-75" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-full p-8 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative w-auto overflow-hidden text-left align-middle transition-all transform rounded">
                  <button
                    onClick={() => onClose()}
                    className="fixed z-10 flex items-center justify-center w-8 h-8 bg-white rounded-full outline-none top-2 right-2"
                  >
                    <XIcon className="w-6 h-6 text-default" />
                  </button>
                  {/*!isLoaded ? (
                    <ContentLoader
                      width={width}
                      height={height}
                      backgroundColor="#009FE3"
                      foregroundColor="#eee"
                    >
                      <rect x="0" y="0" width={width} height={height} />
                    </ContentLoader>
                  ) : null*/}
                  <Image
                    className="rounded"
                    src={image}
                    height={height}
                    width={width}
                    alt="Product Image"
                    onLoad={() => {
                      //setIsLoaded(true);
                    }}
                  />
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
